/**
 * useRelations - the actor's relation to EVERY token, for the map's
 * shootable marks and the pickers' relevance (v8.323.0).
 *
 * `relateFrom` is the pure engine call the area attacks already make at
 * fire time; this asks it once per actor, weapon or token change and hands
 * back the map. Nothing here is stored - cover is a relation, not a
 * property (ADR 2026-07-29) - and nothing here writes: the marks it feeds
 * say who is in range and in sight, and a click still targets anybody.
 *
 * Off (an empty map) when nobody is acting, when the actor has no token,
 * or when the caller says so (building, deploying).
 */
import { useMemo } from 'react';
import { relateFrom } from '../engine/battlefield/relations';

const EMPTY = new Map();

export default function useRelations(params) {
  const { actorId, tokens, solids, weaponRange, isGm = false, enabled = true, relationRules } = params;
  const actorToken = actorId ? tokens?.[actorId] : null;
  return useMemo(() => {
    if (!enabled || !actorToken) return EMPTY;
    const others = Object.values(tokens || {}).filter((t) => t && t.id !== actorToken.id);
    return relateFrom(actorToken, others, solids || [], {
      weaponRange,
      includeHidden: isGm,
      rules: relationRules,
    });
  }, [enabled, actorToken, tokens, solids, weaponRange, isGm, relationRules]);
}

/** The band letters the marks wear; the ledger's words are too long for a marker. */
export const REACH_LETTERS = {
  pointBlank: 'PB',
  short: 'S',
  normal: 'N',
  long: 'L',
  extreme: 'E',
};

/** The cover the shooter sees them behind (T6: the target's cover arc), only when there is some. */
const coverOf = (rel) => (rel.cover?.level && rel.cover.level !== 'none' ? { cover: rel.cover.level } : {});

/**
 * The per-token summary the tokens component draws: a band letter for a
 * token in range and in sight, BLOCKED for one the line cannot reach, and
 * nothing for one beyond every band; and its cover from the shooter (T6).
 */
export function reachFrom(relations) {
  const out = {};
  for (const [id, rel] of relations) {
    if (rel.blocked) out[id] = { band: null, blocked: true };
    else if (rel.rangeBand) out[id] = { band: rel.rangeBand, blocked: false, ...coverOf(rel) };
  }
  return out;
}
